/**
 * THE TOKEN-EXCHANGE SEAM. One interface, because this may have to move off the browser.
 * ===================================================================================
 *
 * ⚠️ **AN OPEN BLOCKER, AND IT IS THE REASON THIS FILE IS SEPARATE FROM `oidc.ts`.**
 * It is **not established whether Federate's token endpoint answers a cross-origin browser `fetch`** —
 * whether it responds to a CORS preflight and returns `Access-Control-Allow-Origin` for a registered web
 * origin. `discovery/auth/idprism.md` §6 records the survey: *every* browser precedent found inside
 * Amazon routes the code-for-token exchange through a server-side hop (`AA-Badge-Pager` posts the code to
 * a Lambda; `AC-Framework` does the whole flow in Lambda@Edge; Tesseract and eRoom use ALB OIDC
 * pre-routing), and no document states the answer either way.
 *
 * **The proxy is NOT built here, deliberately.** Building a token-exchange Lambda on the chance it is
 * needed would be speculative infrastructure with a security surface, for a question one office-hours
 * message answers. What this file does instead is make the swap cheap:
 *
 *  · {@link TokenExchange} is the whole contract — one method, form-encoded in, a classified outcome out.
 *  · {@link directTokenExchange} is the browser implementation: `POST` to the token endpoint, no secret,
 *    no cookies. It is what ships.
 *  · If CORS turns out to be refused, there are two escalating answers and neither touches `oidc.ts`:
 *      1. **A pass-through proxy** that forwards the same form body to Federate and returns the same JSON
 *         — then `VITE_OIDC_TOKEN_ENDPOINT` points at the proxy and **no code changes at all**;
 *      2. **A proxy with a different shape** (JSON body, a session cookie, a BFF that keeps the refresh
 *         token server-side) — then one new `TokenExchange` implementation is written and injected, and
 *         everything about PKCE, state, nonce, expiry, single-flight renewal and the offline behaviour is
 *         untouched.
 *
 * **Either way it stays a public client with no secret.** A proxy is not a reason to introduce one; the
 * `AA-Badge-Pager` shape forwards a PKCE exchange without any client authentication at all.
 *
 * ⚠️ **HOW THE FAILURE WILL LOOK, because it is easy to misdiagnose.** A blocked preflight arrives in
 * JavaScript as `TypeError: Failed to fetch` — no status, no body, indistinguishable from a dropped
 * connection or a captive portal. So it is classified `retryable` below, which is the right call for a
 * dropped connection and the wrong one for CORS: the app will patiently retry something that can never
 * succeed. The distinguishing evidence is not available to us at runtime; it is a refused `OPTIONS` in
 * the network tab. That is stated here rather than papered over with a guess.
 */

/** What the token endpoint answered, classified. Deliberately not a `Response`. */
export type ExchangeOutcome =
  | { readonly ok: true; readonly body: TokenEndpointBody }
  | {
      readonly ok: false;
      readonly code: string;
      readonly detail: string;
      /**
       * True for 5xx, 429 and a rejected `fetch`: these say nothing about the credential. False for 4xx,
       * which is a statement about the request. `oidc.ts` turns that distinction into `session-stale`
       * versus `session-expired`, which is the difference between "wait" and "sign in again".
       */
      readonly retryable: boolean;
    };

/** The fields this app reads from a token response. Everything is `unknown` until checked. */
export interface TokenEndpointBody {
  readonly access_token?: unknown;
  readonly refresh_token?: unknown;
  readonly id_token?: unknown;
  readonly expires_in?: unknown;
  readonly token_type?: unknown;
  readonly scope?: unknown;
  readonly error?: unknown;
  readonly error_description?: unknown;
}

export interface TokenExchange {
  /**
   * Send a form-encoded token request.
   *
   * The caller builds the body — `authorization_code` with a `code_verifier`, or `refresh_token` — and
   * **never includes a `client_secret`**. An implementation must not add one.
   */
  post(form: URLSearchParams): Promise<ExchangeOutcome>;
}

/**
 * The browser implementation: a direct cross-origin `POST`.
 *
 * `credentials: 'omit'` because the only credential in this exchange is the `code_verifier` in the body.
 * Sending cookies to the IdP's origin would be a cross-site credential we neither need nor want to be
 * seen carrying — and this app's own origin also serves the photo redirect.
 */
export function directTokenExchange(tokenEndpoint: string, fetchImpl: typeof fetch): TokenExchange {
  return {
    async post(form) {
      let response: Response;
      try {
        response = await fetchImpl(tokenEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
          credentials: 'omit',
          body: form.toString(),
        });
      } catch (error) {
        // See the file header: a blocked CORS preflight is indistinguishable from a dropped connection
        // here, and is therefore classified the same way. The name says what we actually know.
        return {
          ok: false,
          code: 'token-endpoint-unreachable',
          detail: error instanceof Error ? error.message : String(error),
          retryable: true,
        };
      }

      const parsed = await readJsonBody(response);
      if (response.ok) {
        return { ok: true, body: parsed };
      }

      const code =
        typeof parsed.error === 'string' && parsed.error.length > 0 ? parsed.error : `http-${response.status}`;
      const detail =
        typeof parsed.error_description === 'string' && parsed.error_description.length > 0
          ? parsed.error_description
          : `the sign-in service answered HTTP ${response.status}`;
      return { ok: false, code, detail, retryable: response.status >= 500 || response.status === 429 };
    },
  };
}

async function readJsonBody(response: Response): Promise<TokenEndpointBody> {
  try {
    const text = await response.text();
    if (text.trim().length === 0) {
      return {};
    }
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null ? (parsed as TokenEndpointBody) : {};
  } catch {
    // A proxy error page, or an HTML sign-in page where JSON was expected. The status is still the
    // information, so the caller gets a coded failure rather than an exception.
    return {};
  }
}
